import { Cdr } from '@reliance/types/models/Cdr';
import { toFriendlyCdrBillingCategory } from '@reliance/types/enums/CdrBillingCategory';
import {
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  Row,
  createColumnHelper,
} from '@tanstack/react-table';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { TaxDetailsRow } from './CdrTaxDetails';
import Preview from '../facility/Preview';
import { formatCurrency } from '../format/Currency';
import { formatDuration } from '../format/Duration';
import { formatPhoneNumber } from '../format/PhoneNumber';
import InmateDetailBlock from '../inmate/InmateDetailBlock';
import BaseHtmlTable from '../table/BaseHtmlTable';
import facilityIds from '../table/filters/facilityIds';

export type CdrTableColumnId =
  | 'expand'
  | 'id'
  | 'callTypes'
  | 'setupDate'
  | 'endDate'
  | 'duration'
  | 'direction'
  | 'sourceIds'
  | 'destinations'
  | 'completion'
  | 'termination'
  | 'cost'
  | 'billingCategory'
  | 'originatingPhoneNumber'
  | 'facility'
  | 'inmate';

export const CDR_TABLE_COLUMN_IDS: CdrTableColumnId[] = [
  'expand',
  'id',
  'callTypes',
  'setupDate',
  'endDate',
  'duration',
  'direction',
  'sourceIds',
  'destinations',
  'completion',
  'termination',
  'cost',
  'billingCategory',
  'originatingPhoneNumber',
  'facility',
  'inmate',
];

/** Column meta for useServerTableFilterState. */
export const CDR_TABLE_SERVER_COLUMNS = [
  { id: 'expand' },
  { id: 'id', meta: { sortName: 'id' } },
  { id: 'callTypes', meta: { sortName: 'category' } },
  { id: 'setupDate', meta: { sortName: 'setupDate' } },
  { id: 'endDate', meta: { sortName: 'endDate' } },
  { id: 'duration', meta: { sortName: 'durationSeconds' } },
  { id: 'direction', meta: { sortName: 'direction' } },
  { id: 'sourceIds', meta: { sortName: 'source' } },
  { id: 'destinations', meta: { sortName: 'destination' } },
  { id: 'completion', meta: { sortName: 'completion' } },
  { id: 'termination', meta: { sortName: 'terminationReason' } },
  { id: 'cost', meta: { sortName: 'cost' } },
  { id: 'billingCategory', meta: { sortName: 'billingCategory' } },
  { id: 'originatingPhoneNumber', meta: { sortName: 'originatingPhoneNumber' } },
  { id: 'facility', meta: { sortName: 'facility.name', filterComponent: 'FacilityIdsFilter' } },
  { id: 'inmate', meta: { sortName: 'inmate' } },
];

type CdrTableBaseProps = {
  rows: Cdr[];
  loading?: boolean;
  visibleColumns?: CdrTableColumnId[];
  hiddenColumns?: CdrTableColumnId[];
  pageSizeOptions?: number[];
  /** When true, enables filters on category, source, and destination columns. */
  enableColumnFilters?: boolean;
};

type CdrTableClientProps = CdrTableBaseProps & {
  paginationMode?: 'client';
  perPage?: number;
};

type CdrTableServerProps = CdrTableBaseProps & {
  paginationMode: 'server';
  page?: number;
  totalPages?: number;
  total?: number;
  perPage?: number;
  serverPagination?: PaginationState;
  onServerPaginationChange?: OnChangeFn<PaginationState>;
  serverSorting?: SortingState;
  onServerSortingChange?: OnChangeFn<SortingState>;
  serverColumnFilters?: ColumnFiltersState;
  onServerColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
};

export type CdrTableProps = CdrTableClientProps | CdrTableServerProps;

const formatDateTime = (val: Cdr['setupDate'] | Cdr['endDate']) => {
  if (!val) {
    return '';
  }
  const d = typeof val === 'object' && val && 'date' in val ? (val as { date: string }).date : String(val);
  return moment(d).format('YYYY/MM/DD HH:mm:ss');
};

const CdrTable: React.FC<CdrTableProps> = (props) => {
  const {
    rows,
    loading,
    visibleColumns,
    hiddenColumns,
    pageSizeOptions = [100, 500, 1000],
    enableColumnFilters = false,
  } = props;

  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const columnHelper = createColumnHelper<Cdr>();

  const resolvedHiddenColumns = useMemo((): CdrTableColumnId[] => {
    if (visibleColumns != null && visibleColumns.length > 0) {
      return CDR_TABLE_COLUMN_IDS.filter((columnId) => !visibleColumns.includes(columnId));
    }
    return hiddenColumns ?? [];
  }, [visibleColumns, hiddenColumns]);

  const showExpand = !resolvedHiddenColumns.includes('expand');

  const columnDefs = useMemo(
    () => [
      columnHelper.display({
        id: 'expand',
        header: '',
        enableColumnFilter: false,
        cell: (info) => {
          const isExpanded = expandedRowId === info.row.original.id;
          return (
            <Button
              variant="link"
              size="sm"
              onClick={() => setExpandedRowId(isExpanded ? null : (info.row.original.id ?? null))}
              style={{ padding: 0, textDecoration: 'none', boxShadow: 'none', WebkitBoxShadow: 'none' }}>
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
            </Button>
          );
        },
        size: 30,
      }),
      columnHelper.accessor('id', {
        id: 'id',
        header: 'ID',
        meta: { sortName: 'id' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.category, {
        id: 'callTypes',
        header: 'Category',
        meta: { sortName: 'category' },
        enableColumnFilter: enableColumnFilters,
      }),
      columnHelper.accessor((row) => row.setupDate, {
        id: 'setupDate',
        header: 'Setup',
        cell: (info) => formatDateTime(info.getValue()),
        meta: { sortName: 'setupDate' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.endDate, {
        id: 'endDate',
        header: 'Ended',
        cell: (info) => formatDateTime(info.getValue()),
        meta: { sortName: 'endDate' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.durationSeconds, {
        id: 'duration',
        header: 'Duration',
        cell: (info) => formatDuration(info.getValue() ?? 0),
        meta: { sortName: 'durationSeconds' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.direction, {
        id: 'direction',
        header: 'Direction',
        meta: { sortName: 'direction' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.source, {
        id: 'sourceIds',
        header: 'Source',
        cell: (info) => {
          const row = info.row.original;
          return (
            <>
              {row.source}
              {row.sourceName && <span> ({row.sourceName})</span>}
            </>
          );
        },
        meta: { sortName: 'source' },
        enableColumnFilter: enableColumnFilters,
      }),
      columnHelper.accessor((row) => row.destination, {
        id: 'destinations',
        header: 'Destination',
        cell: (info) => {
          const row = info.row.original;
          const dest = row.destination ?? '';
          return (
            <>
              {dest ? formatPhoneNumber(dest) : ''}
              {row.destinationName && <span> ({row.destinationName})</span>}
            </>
          );
        },
        meta: { sortName: 'destination' },
        enableColumnFilter: enableColumnFilters,
      }),
      columnHelper.accessor((row) => row.completion, {
        id: 'completion',
        header: 'Completion',
        meta: { sortName: 'completion' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.terminationReason, {
        id: 'termination',
        header: 'Termination',
        meta: { sortName: 'terminationReason' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.cost, {
        id: 'cost',
        header: 'Cost',
        cell: (info) => formatCurrency(Number(info.getValue())),
        meta: { sortName: 'cost' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.billingCategory, {
        id: 'billingCategory',
        header: 'Billing',
        cell: (info) => {
          const value = info.getValue();
          return value ? toFriendlyCdrBillingCategory(value) : '';
        },
        meta: { sortName: 'billingCategory' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.originatingPhoneNumber, {
        id: 'originatingPhoneNumber',
        header: 'Originating Phone Number',
        meta: { sortName: 'originatingPhoneNumber' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.facility, {
        id: 'facility',
        header: 'Facility',
        cell: (info) => {
          const facility = info.getValue();
          return facility ? <Preview facility={facility} /> : '';
        },
        filterFn: facilityIds,
        meta: {
          sortName: 'facility.name',
          filterComponent: 'FacilityIdsFilter',
        },
        enableColumnFilter: enableColumnFilters,
      }),
      columnHelper.accessor((row) => row.inmate, {
        id: 'inmate',
        header: 'Inmate',
        cell: (info) => {
          const inmate = info.getValue();
          return inmate ? <InmateDetailBlock inmate={inmate} /> : '';
        },
        meta: { sortName: 'inmate' },
        enableColumnFilter: enableColumnFilters,
      }),
    ],
    [columnHelper, enableColumnFilters, expandedRowId]
  );

  const paginationMode = props.paginationMode ?? 'client';
  const isServer = paginationMode === 'server';
  const serverProps = isServer ? (props as CdrTableServerProps) : null;

  return (
    <BaseHtmlTable
      rows={rows}
      columnDefs={columnDefs}
      hiddenColumns={resolvedHiddenColumns}
      loading={loading}
      pageSizeOptions={pageSizeOptions}
      paginationMode={paginationMode}
      perPage={props.perPage ?? (isServer ? undefined : 1000)}
      page={serverProps?.page}
      totalPages={serverProps?.totalPages}
      total={serverProps?.total}
      serverPagination={serverProps?.serverPagination}
      onServerPaginationChange={serverProps?.onServerPaginationChange}
      serverSorting={serverProps?.serverSorting}
      onServerSortingChange={serverProps?.onServerSortingChange}
      serverColumnFilters={serverProps?.serverColumnFilters}
      onServerColumnFiltersChange={serverProps?.onServerColumnFiltersChange}
      isRowExpanded={
        showExpand ? (row: Row<Cdr>) => expandedRowId === row.original.id : undefined
      }
      getExpansionRow={
        showExpand
          ? (row: Row<Cdr>) => (
              <tr>
                <td
                  colSpan={row.getVisibleCells().length}
                  style={{ padding: 15, backgroundColor: '#f8f9fa' }}>
                  <TaxDetailsRow cdr={row.original} />
                </td>
              </tr>
            )
          : undefined
      }
    />
  );
};

export default CdrTable;
